/**
 * Directory read contracts — RO-3-00f / #44473.
 *
 * Port of `excellent/src/directory/views.tsx` (cause A: own vault sink). The
 * Next view is a server component that calls `listPeople` / `listOrganizations`
 * / `listWorkspaceActivity` / `readEnrichedField` + `ensureWorkspaceSelfEntities`
 * inside its render path. A browser component cannot, so the vault read moves
 * behind these Operations and the view becomes a client component on `useApi`.
 *
 * Two ops match the two compositions the source exports:
 *   - `entities` — people + orgs + the workspace company card fields
 *   - `activity` — cross-product workspace activity rollup seed
 *
 * Self-heal (`ensureWorkspaceSelfEntities`) runs in the entities handler so the
 * Directory still materializes "you" / "your company" the way the Next view did.
 * It is best-effort and does not change the response shape.
 */

import { z } from "zod";
import type { Operation } from "../types";

const personRow = z.object({
  id: z.number(),
  uid: z.string(),
  display_name: z.string(),
  given_name: z.string().nullable(),
  family_name: z.string().nullable(),
  actor_id: z.number().nullable(),
  created_at: z.string(),
  is_self: z.boolean(),
});
export type DirectoryPersonRow = z.infer<typeof personRow>;

const orgRow = z.object({
  id: z.number(),
  uid: z.string(),
  name: z.string(),
  legal_name: z.string().nullable(),
  website: z.string().nullable(),
  created_at: z.string(),
  is_self: z.boolean(),
});
export type DirectoryOrgRow = z.infer<typeof orgRow>;

const companyCard = z.object({
  name: z.string().nullable(),
  hq: z.string().nullable(),
  domain: z.string().nullable(),
});
export type DirectoryCompanyCard = z.infer<typeof companyCard>;

const entitiesResponse = z.object({
  people: z.array(personRow),
  organizations: z.array(orgRow),
  company: companyCard,
});
export type DirectoryEntitiesResponse = z.infer<typeof entitiesResponse>;

const entitiesQuery = z.object({
  workspaceId: z.coerce.number().int().optional(),
});

const entitiesOp: Operation<void, z.infer<typeof entitiesQuery>, void, DirectoryEntitiesResponse> = {
  method: "GET",
  path: () => "/api/directory/entities",
  query: entitiesQuery,
  response: entitiesResponse,
};

/**
 * Grouped activity — the canonical shape `ActivityGroupList` / `groupActivities`
 * produce (`@excellent/core` ActivityGroup). Grouped server-side so the client
 * never imports the bare `@excellent/core` barrel (browser-unsafe).
 *
 * #44545: was `z.custom<Record<string, unknown>>()` (validates nothing) plus a
 * stale `{key,verb,entity_kind,...}` draft. Mirror packages/ui activity contract
 * so the endpoint validates again and the double-cast at DirectoryActivity dies.
 */
const activityGroupMember = z.object({
  id: z.number(),
  entity_id: z.number(),
  occurred_at: z.string(),
  href: z.string(),
  label: z.string().optional(),
});

const activityGroup = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number(),
  latest: z.string(),
  href: z.string(),
  members: z.array(activityGroupMember),
});
export type DirectoryActivityGroup = z.infer<typeof activityGroup>;

const activityResponse = z.object({
  groups: z.array(activityGroup),
});
export type DirectoryActivityResponse = z.infer<typeof activityResponse>;

const activityQuery = z.object({
  workspaceId: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().optional(),
});

const activityOp: Operation<void, z.infer<typeof activityQuery>, void, DirectoryActivityResponse> = {
  method: "GET",
  path: () => "/api/directory/activity",
  query: activityQuery,
  response: activityResponse,
};

// ── resolve — numeric id → uid for legacy /directory/<kind>/<id> deep links ─
//
// Port of `excellent/app/directory/[kind]/[id]/page.tsx` (RO-3-06 / #44299).
// The Next page SELECT'd the uid server-side and redirected to
// `objectHref(uid, "operations")`. A client route needs the same hop as an
// Operation before it can `navigate.replace`.

const resolveQuery = z.object({
  kind: z.enum(["person", "organization"]),
  id: z.coerce.number().int().positive(),
});
export type DirectoryResolveQuery = z.infer<typeof resolveQuery>;

const resolveResponse = z.object({
  uid: z.string().min(1),
  kind: z.enum(["person", "organization"]),
  id: z.number(),
});
export type DirectoryResolveResponse = z.infer<typeof resolveResponse>;

const resolveOp: Operation<
  void,
  DirectoryResolveQuery,
  void,
  DirectoryResolveResponse
> = {
  method: "GET",
  path: () => "/api/directory/resolve",
  query: resolveQuery,
  response: resolveResponse,
};

export const directory = {
  entities: entitiesOp,
  activity: activityOp,
  resolve: resolveOp,
};
